import { BUSINESS, CARRIERS } from "@/data";
import { CallButton } from "./Buttons";
import * as Icons from "lucide-react";

export default function PrepaidService() {
  return (
    <section id="prepaid" data-testid="prepaid-section" className="relative py-20 md:py-28 px-4 md:px-8 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="relative bg-brand-navy text-white rounded-3xl md:rounded-[2rem] overflow-hidden shadow-2xl">
          <div className="absolute inset-0 grid-bg-light opacity-[0.06]" aria-hidden />
          <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-brand-blue/40 blur-[120px]" aria-hidden />
          <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-brand-red/30 blur-[120px]" aria-hidden />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 p-8 md:p-14 lg:p-16">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-blue/15 border border-brand-blue/30 mb-5">
                <Icons.Signal className="w-3.5 h-3.5 text-orange-300" />
                <span className="text-xs font-bold uppercase tracking-widest text-orange-200">Wireless Plans</span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05]">
                Prepaid phone service<br />
                <span className="text-orange-300">without the headache.</span>
              </h2>
              <p className="mt-5 text-base md:text-lg text-white/75 max-w-lg">
                Say GOODBYE to contracts with excessive fees and HELLO to hassle-free FIXED-RATE prepaid plans. We help you choose, activate, and switch — all in-store.
              </p>

              <ul className="mt-8 space-y-3" data-testid="prepaid-list">
                {[
                  "Multiple carriers — pick what fits you",
                  "SIM setup & number transfer help",
                  "New activations on the spot",
                  "Plan changes & upgrades",
                  "Bill payment help (where available)",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-white/90">
                    <Icons.CheckCircle2 className="w-5 h-5 text-brand-red shrink-0 mt-0.5" />
                    <span className="text-sm md:text-base">{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex flex-wrap gap-3">
                <CallButton testid="prepaid-call-btn">Call About Phone Plans</CallButton>
                <a
                  href="#visit"
                  data-testid="prepaid-visit-btn"
                  className="inline-flex items-center gap-2 font-bold rounded-full py-3.5 px-6 bg-white/10 border border-white/20 text-white hover:bg-white/15 transition-all"
                >
                  Visit for Activation Help
                  <Icons.ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Carrier logos panel */}
            <div className="relative">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-300 mb-5">Carriers we work with</div>
              <div className="grid grid-cols-3 sm:grid-cols-3 gap-3 md:gap-4" data-testid="carrier-logos">
                {CARRIERS.map((c) => (
                  <CarrierCard key={c.name} carrier={c} />
                ))}
              </div>
              <div className="mt-5 px-5 py-4 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3">
                <Icons.Plus className="w-5 h-5 text-orange-300 shrink-0" />
                <p className="text-xs md:text-sm text-white/75">
                  Don't see your carrier? Stop in — we work with most prepaid networks.
                </p>
              </div>

              <div className="mt-5 inline-flex items-center gap-2 text-xs text-white/55">
                <Icons.Shield className="w-3.5 h-3.5" />
                <span>No contracts · No credit checks · Same-day activation</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CarrierCard({ carrier }) {
  return (
    <div
      data-testid={`carrier-${carrier.name.toLowerCase().replace(/\s+/g, "-")}`}
      className="group flex flex-col"
    >
      {/* Square logo card — edge-to-edge */}
      <div
        className="relative aspect-square rounded-2xl overflow-hidden ring-1 ring-white/15 group-hover:ring-white/40 group-hover:scale-[1.03] transition-all shadow-lg"
        style={{ background: carrier.bg }}
      >
        <img
          src={carrier.logo}
          alt={`${carrier.name} logo`}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      {/* Carrier name caption */}
      <div className="mt-2.5 text-center">
        <div className="text-[11px] md:text-xs font-bold uppercase tracking-[0.08em] text-white truncate">
          {carrier.name}
        </div>
      </div>
    </div>
  );
}
